import { useCallback, useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Check, Trash2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { FoldCard } from './FoldCard';
import { useAuth, hasRole } from '../lib/auth';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';

/**
 * LA AUTORIZACIÓN DEL HOMEOWNER PARA PAGARLE AL CONTRATISTA.
 *
 * Pedido del dueño (2026-08-08): "que estén en la sección de payments tanto en el claim como en el
 * drawer que abre a la derecha; que sea fácil de encontrar y ver y ESCUCHAR".
 *
 * Ese "escuchar" es literal: la autorización casi siempre es una GRABACIÓN — el homeowner
 * autorizando por teléfono. Vivía suelta en 80 líneas dentro del tab Payments de `ClaimDetail`, y
 * en el drawer NO EXISTÍA: quien abría el claim desde una cola tenía que salir a la pantalla
 * completa para oírla. Este componente es esas 80 líneas extraídas, para que las dos vistas
 * muestren lo mismo y no se separen con el próximo cambio.
 *
 * DOS DECISIONES QUE VALEN LA PENA:
 *
 * 1. LA GRABACIÓN SE VE SIEMPRE, sin desplegar nada. Es lo que se viene a oír acá. Los PDF sí
 *    quedan detrás de un botón (ocupan media pantalla) y las imágenes —captura de un mensaje— se
 *    muestran directo.
 * 2. EL AUDIO SE SIRVE POR `/contractor-authorization/file`, NO POR LA URL FIRMADA. La firmada de
 *    R2 dura 15 minutos; el drawer del claim se deja abierto y pasado ese rato el reproductor
 *    dejaba de cargar y parecía que la grabación se había perdido. El endpoint redirige y firma en
 *    cada request, así que el `src` no caduca. `preload="none"` para no bajar el audio de cada
 *    claim que alguien abre — mismo patrón que las grabaciones de llamadas.
 */

/** ¿El nombre del archivo dice que es audio? Es lo único que tenemos: R2 no guarda el mime. */
export function esAudio(name?: string | null): boolean {
  return /\.(mp3|m4a|wav|ogg|aac|opus|amr|flac|webm)$/i.test(name ?? '');
}
export function esImagen(name?: string | null): boolean {
  return /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(name ?? '');
}

type Auth = {
  hasAuth: boolean; url: string | null; name: string | null;
  /** Quién la subió y cuándo. Sale del evento `payment.contractor_auth_set`, así que hay autoría
   *  también para las que se subieron antes de que esto existiera. */
  uploadedBy: string | null; uploadedAt: string | null;
};

export function ContractorAuthBox(
  { claimId, contractorName, authRequested, onChanged, compacto = false }: {
    claimId: string;
    contractorName: string | null | undefined;
    /** `claims.contractor_auth_requested` — para mostrar "ya se pidió" en vez del botón. */
    authRequested?: boolean;
    onChanged?: () => void;
    /** En el drawer el espacio es la mitad: se achica el visor, no se esconde nada. */
    compacto?: boolean;
  },
) {
  const { t } = useI18n();
  const { me } = useAuth();
  const puedeEditar = hasRole(me, 'manager');
  const [auth, setAuth] = useState<Auth | null>(null);
  const [pedida, setPedida] = useState(Boolean(authRequested));
  const [abierto, setAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { fallo, marcar, limpiar } = useFallo();

  const cargar = useCallback(() => {
    // 403 esperado para quien no es manager: la caja simplemente dice "pendiente".
    api.get<Auth>(`/api/claims/${claimId}/contractor-authorization`)
      .then((a) => { limpiar(); setAuth(a); }).catch((e) => { marcar(e); setAuth(null); });
  }, [claimId]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPedida(Boolean(authRequested)); }, [authRequested]);

  async function subir(file: File) {
    setErr(null); setSubiendo(true);
    try {
      const key = await uploadFile(file, 'authorizations');
      await api.post(`/api/claims/${claimId}/contractor-authorization`, { url: key, name: file.name });
      cargar(); onChanged?.();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setSubiendo(false); }
  }
  async function borrar() {
    if (!window.confirm(t('cd.authDeleteConfirm'))) return;
    try { await api.del(`/api/claims/${claimId}/contractor-authorization`); cargar(); onChanged?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function pedir() {
    try { await api.post(`/api/claims/${claimId}/contractor-authorization-request`, {}); setPedida(true); onChanged?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function cancelarPedido() {
    try { await api.post(`/api/claims/${claimId}/contractor-authorization-request-cancel`, {}); setPedida(false); onChanged?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (!contractorName) return null; // sin contratista no hay a quién autorizarle el pago

  const audio = auth?.hasAuth && esAudio(auth.name);
  const imagen = auth?.hasAuth && esImagen(auth.name);
  /** El audio NO usa `auth.url` (firmada, caduca a los 15 min) sino el endpoint que redirige. */
  const srcAudio = `/api/claims/${claimId}/contractor-authorization/file`;

  return (
    /* Plegada cuando la autorización YA está (no hay nada que hacer ahí); abierta mientras falte.
       El hint dice cuál de los dos casos es sin necesidad de abrirla. */
    <FoldCard
      id="pay-contractorauth"
      title={t('cd.contractorAuthTitle')}
      defaultOpen={auth ? !auth.hasAuth : false}
      hint={auth ? (auth.hasAuth ? t('cd.contractorAuthorized') : t('cd.contractorAuthPending')) : undefined}
    >
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 10 }}>{t('cd.contractorAuthHelp')}</div>
      {fallo && <NoCargo onRetry={cargar} compact />}
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{err}</div>}

      {auth?.hasAuth ? (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--green)', fontWeight: 'var(--fw-semibold)' }}>
              <Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.contractorAuthorized')}
            </span>
            {auth.name && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{auth.name}</span>}
            {/* QUIÉN LA SUBIÓ. Sin esto, un archivo equivocado —el caso real fue un buzón de voz en
                vez de la autorización— no tenía dueño, y ni una super-manager podía saber si el
                error había sido suyo. */}
            {auth.uploadedBy && (
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {t('cd.authUploadedBy', { who: auth.uploadedBy })}
                {auth.uploadedAt ? ` · ${new Date(auth.uploadedAt).toLocaleString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
              </span>
            )}
            {auth.url && !audio && !imagen && (
              <button className="btn ghost sm" onClick={() => setAbierto(!abierto)}>
                {abierto ? t('common.hide') : t('cd.viewAuth')}
              </button>
            )}
            {puedeEditar && (
              <FileDrop onFile={subir} accept="application/pdf,image/*,audio/*"
                label={t('cd.contractorAuthReplace')} busy={subiendo} busyLabel={t('cd.uploading')} compact />
            )}
            {puedeEditar && (
              <button className="btn ghost sm" style={{ color: 'var(--red)' }} onClick={borrar}>
                <Trash2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chk.delete')}
              </button>
            )}
          </div>

          {/* La GRABACIÓN, siempre visible: es lo que se viene a oír. */}
          {audio && <audio controls preload="none" src={srcAudio} style={{ width: '100%', marginTop: 8, height: 34 }} />}
          {imagen && auth.url && (
            <img src={auth.url} alt={auth.name ?? 'authorization'}
              style={{ maxWidth: '100%', maxHeight: compacto ? 220 : 320, borderRadius: 8, border: '1px solid var(--line)', display: 'block', marginTop: 8 }} />
          )}
          {abierto && auth.url && !audio && !imagen && (
            <div style={{ marginTop: 10 }}>
              <iframe title="auth" src={auth.url}
                style={{ width: '100%', height: compacto ? 300 : 420, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--card)' }} />
            </div>
          )}
        </>
      ) : puedeEditar ? (
        <>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>
            {t('cd.authUploadLabel')} <strong>{contractorName}</strong>:
          </div>
          <FileDrop onFile={subir} accept="application/pdf,image/*,audio/*"
            label={t('cd.contractorAuthDrop')} busy={subiendo} busyLabel={t('cd.uploading')} />
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {pedida ? (
              <>
                <span className="badge orange">
                  <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.authRequested')}
                </span>
                <button className="btn ghost sm" style={{ color: 'var(--red)' }} onClick={cancelarPedido}>{t('cd.authCancel')}</button>
              </>
            ) : (
              // La autorización la da el HOMEOWNER (antes decía el nombre del contratista y confundía).
              <button className="btn ghost sm" onClick={pedir}>{t('cd.authRequest')}</button>
            )}
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cd.authRequestHelp')}</span>
          </div>
        </>
      ) : (
        <span className="muted">{t('cd.contractorAuthPending')}</span>
      )}
    </FoldCard>
  );
}
